// ** React Imports
import React, { useEffect, useState } from 'react'

// ** MUI Imports
import { Button, Grid, Typography } from '@mui/material'
import { GridColumns, GridRenderCellParams } from '@mui/x-data-grid-pro'

// ** Child Imports
import { ArrowDownRight, ArrowUpRight, Download, Minus, TrendingDown, TrendingUp } from 'react-feather'
import FloDataGrid from 'src/components/common/data-grid/FloDataGrid'

// ** Util Imports
import { WidgetType } from 'src/lib/types/dashboardAnalytics'
import { arrayToCsv, downloadCsvHelper, numberWithCommas } from 'src/lib/utils/helpers'
import { useFilter } from 'src/lib/contexts/FilterContext'
import { extractDateFromFilters, getAnalyticsWidgetPayload } from 'src/lib/utils/analyticsHelpers'
import { analyticsConstants } from 'src/lib/utils/constants'

const DiscountFailureRatesTable: React.FC<WidgetType> = ({
  value,
  hideDownloadReport = true,
  isLoading,
  error,
  triggerRefresh,
  triggerDownloadReport,
  downloadTriggered,
  hideBorder = false,
  hideRowDivider = false,
  isPaginationEnabled = true,
  hideHeader = false,
  hideFooter = false,
  removeCellPadding = false,
  downloadReportPosition = { top: -98, right: 0 },
  isReportView = false,
  moreIsGood,
  hasComparison
}) => {
  const { analyticsFilters } = useFilter()

  const [page, setPage] = useState<number>(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(10)

  useEffect(() => {
    if (typeof value !== 'undefined') setPage(0)
  }, [value])

  useEffect(() => {
    if (downloadTriggered) {
      downloadCsvHelper(
        arrayToCsv(value, ['Discount code', 'Discount selected', 'Discount code entered', 'Failed application']),
        'discount_failure_report.csv'
      )
      typeof triggerDownloadReport !== 'undefined' && triggerDownloadReport(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadTriggered])

  const handlePageChange = async (newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (pageSize: number) => {
    triggerRefresh(
      getAnalyticsWidgetPayload(
        analyticsConstants.FLO_DISCOUNT_FAILURE_RATES,
        extractDateFromFilters(analyticsFilters, 'startDate'),
        extractDateFromFilters(analyticsFilters, 'endDate')
      )
    )
    setRowsPerPage(pageSize)
    setPage(0)
  }

  const widgetColumns: GridColumns = [
    {
      field: 'code',
      headerName: 'Discount code',
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        const row = params?.row
        const value = row['code']
        const sececondayValue = row['discounts_selected'] + row['codes_entered']

        return (
          <div className='flex flex-col w-52'>
            <Typography
              variant='body2'
              sx={{
                fontWeight: 400,
                color: 'text.header',
                lineHeight: '18px',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {value}
            </Typography>
            <Typography variant='body2' sx={{ color: '#949494', lineHeight: '18px' }}>
              {numberWithCommas(sececondayValue, 0)} attempts
            </Typography>
          </div>
        )
      }
    },
    {
      field: 'failure_rate',
      headerName: 'Failure rate',
      maxWidth: 90,
      width: 90,
      minWidth: 90,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        const row = params?.row
        const value = row['failure_rate']
        return (
          <p
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'start',
              margin: 0,
              fontWeight: 500,
              color: '#4d4d4d'
            }}
          >
            <span>{value}</span>
          </p>
        )
      }
    }
    // {
    //   field: 'compareToVal',
    //   maxWidth: hasComparison ? 70 : 0,
    //   width: hasComparison ? 70 : 0,
    //   minWidth: hasComparison ? 70 : 0,
    //   sortable: false,
    //   renderCell: (params: GridRenderCellParams<any>) => {
    //     const row = params?.row
    //     const value = row['compareToVal']
    //     const isGood = moreIsGood ? Boolean(parseInt(value) > 0) : Boolean(parseInt(value) < 0)

    //     return hasComparison ? (
    //       <p
    //         style={{
    //           width: '100%',
    //           display: 'flex',
    //           justifyContent: 'end',
    //           alignItems: 'center',
    //           margin: 0,
    //           gap: 2,
    //           fontWeight: 600,
    //           color: isGood ? '#2C874A' : '#CB2711'
    //         }}
    //       >
    //         {Boolean(parseInt(value)) && !value.includes('Infinity') ? (
    //           isGood ? (
    //             <ArrowDownRight className='h-4 w-4 stroke-[2.5px]' />
    //           ) : (
    //             <ArrowUpRight className='h-4 w-4 stroke-[2.5px]' />
    //           )
    //         ) : (
    //           <></>
    //         )}
    //         {Boolean(parseInt(value)) && !value.includes('Infinity') ? (
    //           <>
    //             <> {Math.sign(parseInt(value)) === -1 ? value?.slice(1) : value}</>
    //           </>
    //         ) : (
    //           <Minus className='text-[#949494] w-[14px] stroke-[2.5px]' />
    //         )}
    //       </p>
    //     ) : (
    //       <></>
    //     )
    //   }
    // }
  ]

  const reportColumns: GridColumns = [
    {
      field: 'code',
      headerName: 'Discount code',
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        const row = params?.row
        const value = row['code']

        return (
          <div className='flex flex-col w-52'>
            <Typography
              variant='body2'
              sx={{
                fontWeight: 400,
                color: 'text.header',
                lineHeight: '18px',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {value}
            </Typography>
          </div>
        )
      }
    },
    {
      field: 'discounts_selected',
      headerName: 'Discount selected',
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        const row = params?.row
        const value = row['discounts_selected']
        return (
          <Typography
            variant='body2'
            sx={{
              fontWeight: 400,
              color: 'text.header',
              lineHeight: '18px',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {value}
          </Typography>
        )
      }
    },
    {
      field: 'codes_entered',
      headerName: 'Discount code entered',
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        const row = params?.row
        const value = row['codes_entered']
        return (
          <Typography
            variant='body2'
            sx={{
              fontWeight: 400,
              color: 'text.header',
              lineHeight: '18px',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {value}
          </Typography>
        )
      }
    },
    {
      field: 'failure_rate',
      headerName: 'Failed applications',
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => {
        const row = params?.row
        const value = row['failure_rate']
        const secondaryValue = row['failed_attempts']
        return (
          <div className='flex justify-between w-full'>
            <Typography
              variant='body2'
              sx={{
                fontWeight: 400,
                color: 'text.header',
                lineHeight: '18px',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {secondaryValue}
            </Typography>
            <Typography
              variant='body2'
              sx={{
                fontWeight: 400,
                color: 'text.header',
                lineHeight: '18px',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {value}
            </Typography>
          </div>
        )
      }
    }
  ]

  return (
    <div className='flex flex-col w-full'>
      <Grid item sx={{ width: '100%', position: 'relative' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            width: '50%',
            justifyContent: 'end',
            position: 'absolute',
            top: downloadReportPosition?.top,
            right: downloadReportPosition?.right
          }}
        >
          {!Boolean(hideDownloadReport) ? (
            <Button
              variant='outlined'
              sx={{ height: 36, position: 'absolute', top: -130 }}
              disabled={!value?.length}
              onClick={() => {
                downloadCsvHelper(
                  arrayToCsv(value, [
                    'Discount code',
                    'Discount selected',
                    'Discount code entered',
                    'Failed application',
                    'Failure rate'
                  ]),
                  'discount_failure_report.csv'
                )
              }}
            >
              <Download style={{ transform: 'scale(0.75)' }} />
              Download report
            </Button>
          ) : (
            <></>
          )}
        </div>
      </Grid>
      <FloDataGrid
        page={page}
        changePage={handlePageChange}
        rowsPerPage={rowsPerPage}
        rowHeight={48}
        headerHeight={48}
        totalRowCount={value?.length ?? 0}
        rows={
          isReportView
            ? [...value?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)]
            : [...value?.slice(page * 5, page * 5 + 5)]
        }
        loading={isLoading}
        columns={isReportView ? reportColumns : widgetColumns}
        variant={isReportView ? 'grid' : 'table'}
        hideBorder={hideBorder}
        hideRowDivider={hideRowDivider}
        pagination={isPaginationEnabled}
        hideHeader={hideHeader}
        hideFooter={hideFooter}
        rowsPerPageOptions={[10, 25, 50]}
        {...(isReportView ? { pinnedColumns: { left: ['code'] } } : {})}
        changeRowsPerPage={handleChangeRowsPerPage}
        pageSizeOptionsForDropdown={[
          {
            value: 10,
            label: '10'
          },
          {
            value: 25,
            label: '25'
          },
          {
            value: 50,
            label: '50'
          }
        ]}
        removeCellPadding={removeCellPadding}
        noDataJXSElement={
          <Typography variant='subtitle2' sx={{ mt: 2 }}>
            <span style={{ fontWeight: 500, color: 'text.header' }}>No data available.</span>
          </Typography>
        }
      />
    </div>
  )
}

export default DiscountFailureRatesTable
